// The passkey (WebAuthn) ceremony, factored into one place so the views stay
// thin (CLAUDE.md DRY). @simplewebauthn/browser does the base64url<->ArrayBuffer
// conversion and cross-browser quirk handling: it consumes the JSON options PMS
// relays from Cognito and returns post-ready JSON. PMS never sees a secret — the
// browser + the authenticator do the crypto; PMS only relays start/verify.

import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from "@simplewebauthn/browser";
import {
  startPasskeyRegistration,
  completePasskeyRegistration,
  startPasskeyLogin,
  verifyPasskeyLogin,
  type AuthedRequester,
  type AuthTokens,
} from "@/lib/brokerAuth";
import { ApiError } from "@/lib/api";

/** Whether this browser can do WebAuthn at all (drives feature-detection). */
export function isPasskeySupported(): boolean {
  return browserSupportsWebAuthn();
}

/**
 * Register a new passkey for the authenticated broker: fetch creation options,
 * run navigator.credentials.create() (via startRegistration), and register the
 * attestation. Broker-guarded — goes through the authed requester.
 */
export async function registerPasskey(request: AuthedRequester): Promise<void> {
  const { options } = await startPasskeyRegistration(request);
  const credential = await startRegistration({ optionsJSON: options });
  await completePasskeyRegistration(request, credential);
}

/**
 * Sign in with a passkey: fetch request options for the email, run
 * navigator.credentials.get() (via startAuthentication), and verify the
 * assertion. Public — returns tokens like a password sign-in.
 */
export async function signInWithPasskey(email: string): Promise<AuthTokens> {
  const { username, session, options } = await startPasskeyLogin(email);
  const credential = await startAuthentication({ optionsJSON: options });
  return verifyPasskeyLogin({ username, session, credential });
}

/**
 * True when the ceremony was dismissed/aborted by the user (or timed out) rather
 * than failing — the browser raises a NotAllowedError, which @simplewebauthn
 * surfaces directly or as the `cause` of a WebAuthnError. The UI treats this as a
 * quiet reset (no scary alert), not an error to report.
 */
export function isPasskeyCancellation(err: unknown): boolean {
  const code = (err as { code?: string } | null)?.code;
  if (code === "ERROR_CEREMONY_ABORTED") return true;
  const names = [err, (err as { cause?: unknown } | null)?.cause].map((e) =>
    e instanceof Error ? e.name : "",
  );
  return names.includes("NotAllowedError") || names.includes("AbortError");
}

/**
 * A user-facing message for a failed passkey ceremony, or null when the user
 * simply cancelled (the caller should reset quietly instead of showing an alert).
 * PMS relay failures carry a client-safe `{ error }` message; surface it.
 */
export function describePasskeyError(err: unknown): string | null {
  if (isPasskeyCancellation(err)) return null;
  if (err instanceof ApiError) return err.message;
  return "Your passkey couldn’t be used. Please try again.";
}
